import React, { useEffect, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { FaDownload, FaChevronUp, FaChevronDown, FaTimes, FaSpinner } from 'react-icons/fa';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const MobilePDFViewer = ({ pdfUrl, title = "PDF Document", onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Convert PDF pages to canvas images
  const renderPageToCanvas = async (page, canvas) => {
    const context = canvas.getContext('2d');
    
    // Mobile optimized: use screen width
    const screenWidth = window.innerWidth - 40; // Account for padding
    const viewport = page.getViewport({ scale: 1.0 });
    const scale = screenWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale: scale * 2 }); // 2x for high DPI
    
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    
    const renderContext = {
      canvasContext: context,
      viewport: scaledViewport,
    };
    
    await page.render(renderContext).promise;
    return canvas.toDataURL('image/png');
  };

  // Load PDF and convert pages to images
  useEffect(() => {
    if (!pdfUrl) return;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const loadingTask = pdfjs.getDocument({
          url: pdfUrl,
          cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
          cMapPacked: true,
        }).promise;
        
        const pdf = await loadingTask;
        const totalPages = pdf.numPages;
        setNumPages(totalPages);

        const images = [];

        // Load all pages
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const canvas = document.createElement('canvas');
          const imageUrl = await renderPageToCanvas(page, canvas);
          images.push(imageUrl);
        }

        setPageImages(images);
        setCurrentPage(1);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF document. Please try again or download the file.');
        setLoading(false);
      }
    };

    loadPDF();
  }, [pdfUrl]);

  const handleDownload = async () => {
    try {
      // Fetch the PDF as a blob to force download
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Set download attribute with sanitized filename
      const sanitizedTitle = (title || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `${sanitizedTitle}.pdf`;
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback: open in new tab if download fails
      window.open(pdfUrl, '_blank');
    }
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(numPages, prev + 1));
  };

  // Add mobile-specific styles
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const styleId = 'mobile-pdf-viewer-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .mobile-pdf-viewer-container {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.95);
        z-index: 9999;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .mobile-pdf-viewer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.75rem 1rem;
        background: #1f2937;
        color: white;
        flex-shrink: 0;
      }

      .mobile-pdf-viewer-title {
        font-size: 1rem;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        margin-right: 0.5rem;
      }

      .mobile-pdf-viewer-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: #111827;
        position: relative;
        overflow: hidden;
        padding: 1rem;
      }

      .mobile-pdf-viewer-page-container {
        width: 100%;
        max-width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
        overflow: hidden;
      }

      .mobile-pdf-viewer-page-image {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
        display: block;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
      }

      .mobile-pdf-viewer-nav {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        background: #1f2937;
      }

      .mobile-pdf-viewer-nav-buttons {
        display: flex;
        gap: 0.5rem;
        width: 100%;
        max-width: 300px;
      }

      .mobile-pdf-viewer-nav-button {
        flex: 1;
        padding: 1rem;
        background: #374151;
        color: white;
        border: none;
        border-radius: 0.5rem;
        font-size: 1rem;
        font-weight: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        transition: background 0.2s;
        min-height: 56px;
      }

      .mobile-pdf-viewer-nav-button:active:not(:disabled) {
        background: #4b5563;
      }

      .mobile-pdf-viewer-nav-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .mobile-pdf-viewer-page-info {
        color: white;
        font-size: 0.875rem;
        font-weight: 500;
        text-align: center;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      .animate-spin {
        animation: spin 1s linear infinite;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= numPages;

  if (error) {
    return (
      <div className="mobile-pdf-viewer-container">
        <div className="mobile-pdf-viewer-header">
          <h3 className="mobile-pdf-viewer-title">{title}</h3>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: 'white',
              }}
              title="Close"
            >
              <FaTimes size={20} />
            </button>
          )}
        </div>
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>
          <button
            onClick={handleDownload}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.75rem 1.5rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '500',
              gap: '0.5rem',
            }}
          >
            <FaDownload />
            Download PDF
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-pdf-viewer-container">
      {/* Header */}
      <div className="mobile-pdf-viewer-header">
        <h3 className="mobile-pdf-viewer-title">{title}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={handleDownload}
            style={{
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              color: 'white',
            }}
            title="Download PDF"
          >
            <FaDownload size={20} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: 'white',
              }}
              title="Close"
            >
              <FaTimes size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mobile-pdf-viewer-content">
        {loading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '1rem',
            color: 'white',
          }}>
            <FaSpinner className="animate-spin" size={32} />
            <p>Loading PDF pages...</p>
          </div>
        ) : pageImages.length > 0 ? (
          <>
            <div className="mobile-pdf-viewer-page-container">
              <img
                src={pageImages[currentPage - 1]}
                alt={`Page ${currentPage}`}
                className="mobile-pdf-viewer-page-image"
              />
            </div>
          </>
        ) : null}
      </div>

      {/* Navigation Footer */}
      {!loading && pageImages.length > 0 && (
        <div className="mobile-pdf-viewer-nav">
          <div className="mobile-pdf-viewer-page-info">
            Page {currentPage} of {numPages}
          </div>
          <div className="mobile-pdf-viewer-nav-buttons">
            <button
              onClick={goToPrevPage}
              disabled={isFirstPage}
              className="mobile-pdf-viewer-nav-button"
              title="Previous Page"
            >
              <FaChevronUp size={20} />
              Previous
            </button>
            <button
              onClick={goToNextPage}
              disabled={isLastPage}
              className="mobile-pdf-viewer-nav-button"
              title="Next Page"
            >
              Next
              <FaChevronDown size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePDFViewer;

